import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../../hooks';
import { useWorkspace } from '../../hooks/useWorkspace';
import { useWorkspaceState } from '../../hooks/useWorkspaceState';
import { useAdminDocumentTypes } from '../../hooks/useAdminDocumentTypes';
import { useAdminRoles } from '../../hooks/useAdminRoles';
import { useAdminTemplates } from '../../hooks/useAdminTemplates';
import { useAdminWorkflows } from '../../hooks/useAdminWorkflows';
import { useAdminSchemas } from '../../hooks/useAdminSchemas';
import usePromptFragments from '../../hooks/usePromptFragments';
import { adminApi } from '../../api/adminClient';
import DocTypeBrowser from './DocTypeBrowser';
import BuildingBlocksTray from './BuildingBlocksTray';
import PromptEditor from './PromptEditor';
import RoleEditor from './RoleEditor';
import TemplateEditor from './TemplateEditor';
import SchemaEditor from './SchemaEditor';
import PromptFragmentEditor from './PromptFragmentEditor';
import MechanicalOpEditor from './MechanicalOpEditor';
import StepWorkflowEditor from './workflow/StepWorkflowEditor';
import GitStatusPanel from './GitStatusPanel';

/**
 * Admin Workbench - main layout for document type prompt editing.
 *
 * Three-panel layout:
 * - Left (240px): Document type and role browser
 * - Center (flex-1): Prompt editor with tabs
 * - Right (320px): Git status and commit actions
 *
 * Requires admin role.
 */
export default function AdminWorkbench() {
    const { isAdmin, loading: authLoading } = useAuth();
    const [selectedDocType, setSelectedDocType] = useState(null);
    const [selectedDocTypeDetails, setSelectedDocTypeDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [selectedWorkflow, setSelectedWorkflow] = useState(null);
    const [selectedFragment, setSelectedFragment] = useState(null);
    const [selectedSchema, setSelectedSchema] = useState(null);
    const [initialTab, setInitialTab] = useState(null);
    const [docTypeSource, setDocTypeSource] = useState(null); // 'docworkflow' | 'task' | 'schema'
    const [isTrayOpen, setIsTrayOpen] = useState(false); // Building Blocks tray (WS-ADR-044-003)

    // Workspace lifecycle
    const {
        workspaceId,
        loading: workspaceLoading,
        error: workspaceError,
        reinitialize,
    } = useWorkspace();

    // Workspace state (git status, validation)
    const {
        state: workspaceState,
        loading: stateLoading,
        refresh: refreshState,
    } = useWorkspaceState(workspaceId);

    // Document types list
    const {
        documentTypes,
        loading: docTypesLoading,
        refresh: refreshDocTypes,
    } = useAdminDocumentTypes();

    // Roles list
    const {
        roles,
        loading: rolesLoading,
        refresh: refreshRoles,
    } = useAdminRoles();

    // Templates list
    const {
        templates,
        loading: templatesLoading,
        refresh: refreshTemplates,
    } = useAdminTemplates();

    // Workflows list
    const {
        workflows,
        loading: workflowsLoading,
        refresh: refreshWorkflows,
    } = useAdminWorkflows();

    // Prompt fragments (unified view)
    const {
        fragments: promptFragments,
        loading: promptFragmentsLoading,
        kindOptions: promptFragmentKindOptions,
        refresh: refreshFragments,
    } = usePromptFragments();

    // Standalone schemas
    const {
        schemas,
        loading: schemasLoading,
        refresh: refreshSchemas,
    } = useAdminSchemas();

    // Mechanical operations (ADR-047)
    const [mechanicalOpTypes, setMechanicalOpTypes] = useState([]);
    const [mechanicalOps, setMechanicalOps] = useState([]);
    const [mechanicalOpsLoading, setMechanicalOpsLoading] = useState(false);
    const [selectedMechanicalOp, setSelectedMechanicalOp] = useState(null);

    // Load mechanical ops
    const refreshMechanicalOps = useCallback(async () => {
        setMechanicalOpsLoading(true);
        try {
            const [typesRes, opsRes] = await Promise.all([
                adminApi.getMechanicalOpTypes(),
                adminApi.getMechanicalOps(),
            ]);
            setMechanicalOpTypes(typesRes.types || []);
            setMechanicalOps(opsRes.operations || []);
        } catch (err) {
            console.error('Failed to load mechanical ops:', err);
        } finally {
            setMechanicalOpsLoading(false);
        }
    }, []);

    // Load mechanical ops on mount
    useEffect(() => {
        refreshMechanicalOps();
    }, [refreshMechanicalOps]);

    // Handle doc type selection - fetch full details
    const handleSelectDocType = useCallback(async (docType, tab = null, source = 'docworkflow') => {
        setSelectedDocType(docType);
        setSelectedRole(null);
        setSelectedTemplate(null);
        setSelectedWorkflow(null);
        setSelectedDocTypeDetails(null);
        setSelectedFragment(null);
        setSelectedSchema(null);
        setSelectedMechanicalOp(null);
        setInitialTab(tab);
        setDocTypeSource(source);

        if (!docType) return;

        setDetailsLoading(true);
        try {
            const details = await adminApi.getDocumentType(docType.doc_type_id);
            setSelectedDocTypeDetails(details);
        } catch (err) {
            console.error('Failed to load doc type details:', err);
            // Still show the doc type but with limited info
            setSelectedDocTypeDetails(docType);
        } finally {
            setDetailsLoading(false);
        }
    }, []);

    // Handle task/interaction selection - loads PromptEditor on task_prompt tab
    const handleSelectTask = useCallback((docType) => {
        handleSelectDocType(docType, 'task_prompt', 'task');
    }, [handleSelectDocType]);

    // Handle schema selection - loads PromptEditor on schema tab
    const handleSelectSchema = useCallback((docType) => {
        handleSelectDocType(docType, 'schema', 'schema');
    }, [handleSelectDocType]);

    // Handle role selection
    const handleSelectRole = useCallback((role) => {
        setSelectedRole(role);
        setSelectedDocType(null);
        setSelectedDocTypeDetails(null);
        setSelectedTemplate(null);
        setSelectedWorkflow(null);
        setSelectedFragment(null);
        setSelectedSchema(null);
        setSelectedMechanicalOp(null);
    }, []);

    // Handle template selection
    const handleSelectTemplate = useCallback((template) => {
        setSelectedTemplate(template);
        setSelectedDocType(null);
        setSelectedDocTypeDetails(null);
        setSelectedRole(null);
        setSelectedWorkflow(null);
        setSelectedFragment(null);
        setSelectedSchema(null);
        setSelectedMechanicalOp(null);
    }, []);

    // Handle workflow selection
    const handleSelectWorkflow = useCallback((workflow) => {
        setSelectedWorkflow(workflow);
        setSelectedDocType(null);
        setSelectedDocTypeDetails(null);
        setSelectedRole(null);
        setSelectedTemplate(null);
        setSelectedFragment(null);
        setSelectedSchema(null);
        setSelectedMechanicalOp(null);
    }, []);

    // Handle prompt fragment selection
    const handleSelectFragment = useCallback((fragment) => {
        setSelectedFragment(fragment);
        setSelectedDocType(null);
        setSelectedDocTypeDetails(null);
        setSelectedRole(null);
        setSelectedTemplate(null);
        setSelectedWorkflow(null);
        setSelectedSchema(null);
        setSelectedMechanicalOp(null);
    }, []);

    // Handle standalone schema selection
    const handleSelectStandaloneSchema = useCallback((schema) => {
        setSelectedSchema(schema);
        setSelectedDocType(null);
        setSelectedDocTypeDetails(null);
        setSelectedRole(null);
        setSelectedTemplate(null);
        setSelectedWorkflow(null);
        setSelectedFragment(null);
        setSelectedMechanicalOp(null);
    }, []);

    // Handle mechanical operation selection (ADR-047)
    const handleSelectMechanicalOp = useCallback((op) => {
        setSelectedMechanicalOp(op);
        setSelectedDocType(null);
        setSelectedDocTypeDetails(null);
        setSelectedRole(null);
        setSelectedTemplate(null);
        setSelectedWorkflow(null);
        setSelectedFragment(null);
        setSelectedSchema(null);
    }, []);

    // Handle navigation to a workflow by ID (e.g., from derived_from link)
    const handleNavigateToWorkflow = useCallback((workflowId) => {
        const wf = workflows.find(w => w.workflow_id === workflowId);
        if (wf) {
            handleSelectWorkflow(wf);
        }
    }, [workflows, handleSelectWorkflow]);

    // Handle artifact save (refresh workspace state)
    const handleArtifactSave = useCallback((artifactId, result) => {
        // Refresh state to update dirty status and validation
        refreshState();
    }, [refreshState]);

    // Handle successful commit
    const handleCommit = useCallback((result) => {
        // State will refresh automatically
    }, []);

    // Handle discard
    const handleDiscard = useCallback(() => {
        // Clear selection and refresh
        setSelectedDocType(null);
        setSelectedDocTypeDetails(null);
        setSelectedRole(null);
        setSelectedTemplate(null);
        setSelectedWorkflow(null);
        setSelectedFragment(null);
        setSelectedSchema(null);
        setSelectedMechanicalOp(null);
    }, []);

    // Handle create workflow
    const handleCreateWorkflow = useCallback(async (data) => {
        if (!workspaceId) return;
        try {
            const result = await adminApi.createOrchestrationWorkflow(workspaceId, data);
            await refreshWorkflows();
            refreshState();
            // Auto-select the new workflow
            setSelectedWorkflow({
                workflow_id: result.workflow_id,
                active_version: result.version,
                name: data.name || result.workflow_id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            });
            setSelectedDocType(null);
            setSelectedDocTypeDetails(null);
            setSelectedRole(null);
            setSelectedTemplate(null);
        } catch (err) {
            console.error('Failed to create workflow:', err);
            alert(`Failed to create workflow: ${err.message}`);
        }
    }, [workspaceId, refreshWorkflows, refreshState]);

    // Handle delete workflow
    const handleDeleteWorkflow = useCallback(async (workflowId) => {
        if (!workspaceId) return;
        try {
            await adminApi.deleteOrchestrationWorkflow(workspaceId, workflowId);
            await refreshWorkflows();
            refreshState();
            setSelectedWorkflow(null);
        } catch (err) {
            console.error('Failed to delete workflow:', err);
            alert(`Failed to delete workflow: ${err.message}`);
        }
    }, [workspaceId, refreshWorkflows, refreshState]);

    // Handle create document type
    const handleCreateDocType = useCallback(async (data) => {
        if (!workspaceId) return;
        try {
            const result = await adminApi.createDocumentType(workspaceId, data);
            await refreshDocTypes();
            refreshState();
            // Auto-select the new document type
            handleSelectDocType({
                doc_type_id: result.doc_type_id,
                display_name: data.display_name || result.doc_type_id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                active_version: result.version,
            });
        } catch (err) {
            console.error('Failed to create document type:', err);
            alert(`Failed to create document type: ${err.message}`);
        }
    }, [workspaceId, refreshDocTypes, refreshState, handleSelectDocType]);

    // Handle delete document type
    const handleDeleteDocType = useCallback(async (docTypeId) => {
        if (!workspaceId) return;
        try {
            await adminApi.deleteDocumentType(workspaceId, docTypeId);
            await refreshDocTypes();
            refreshState();
            setSelectedDocType(null);
            setSelectedDocTypeDetails(null);
        } catch (err) {
            console.error('Failed to delete document type:', err);
            alert(`Failed to delete document type: ${err.message}`);
        }
    }, [workspaceId, refreshDocTypes, refreshState]);

    // Handle create DCW workflow (graph-based workflow for document type)
    const handleCreateDcwWorkflow = useCallback(async (docTypeId) => {
        if (!workspaceId) return;
        try {
            await adminApi.createDcwWorkflow(workspaceId, { doc_type_id: docTypeId });
            await refreshWorkflows();
            refreshState();
        } catch (err) {
            console.error('Failed to create DCW workflow:', err);
            alert(`Failed to create workflow: ${err.message}`);
        }
    }, [workspaceId, refreshWorkflows, refreshState]);

    // Handle create prompt fragment (role prompt)
    const handleCreateFragment = useCallback(async (data) => {
        if (!workspaceId) return;
        try {
            const result = await adminApi.createRolePrompt(workspaceId, data);
            await refreshFragments();
            await refreshRoles();
            refreshState();
            // Auto-select the new fragment
            handleSelectFragment({
                fragment_id: `role:${result.role_id}`,
                kind: 'role',
                version: result.version,
                name: data.name || result.role_id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            });
        } catch (err) {
            console.error('Failed to create role prompt:', err);
            alert(`Failed to create role prompt: ${err.message}`);
        }
    }, [workspaceId, refreshFragments, refreshRoles, refreshState, handleSelectFragment]);

    // Handle create template
    const handleCreateTemplate = useCallback(async (data) => {
        if (!workspaceId) return;
        try {
            const result = await adminApi.createTemplate(workspaceId, data);
            await refreshTemplates();
            refreshState();
            // Auto-select the new template
            handleSelectTemplate({
                template_id: result.template_id,
                active_version: result.version,
                name: data.name || result.template_id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            });
        } catch (err) {
            console.error('Failed to create template:', err);
            alert(`Failed to create template: ${err.message}`);
        }
    }, [workspaceId, refreshTemplates, refreshState, handleSelectTemplate]);

    // Handle create standalone schema
    const handleCreateSchema = useCallback(async (data) => {
        if (!workspaceId) return;
        try {
            const result = await adminApi.createStandaloneSchema(workspaceId, data);
            await refreshSchemas();
            refreshState();
            // Auto-select the new schema
            handleSelectStandaloneSchema({
                schema_id: result.schema_id,
                active_version: result.version,
                title: data.title || result.schema_id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            });
        } catch (err) {
            console.error('Failed to create schema:', err);
            alert(`Failed to create schema: ${err.message}`);
        }
    }, [workspaceId, refreshSchemas, refreshState, handleSelectStandaloneSchema]);

    // Handle workspace close
    const handleClose = useCallback(() => {
        // Reinitialize (will create new workspace)
        reinitialize();
        setSelectedDocType(null);
        setSelectedDocTypeDetails(null);
        setSelectedRole(null);
        setSelectedTemplate(null);
        setSelectedWorkflow(null);
        setSelectedFragment(null);
        setSelectedSchema(null);
        setSelectedMechanicalOp(null);
    }, [reinitialize]);

    // Handle reload config (invalidate server cache and refresh all data)
    const handleReloadConfig = useCallback(async () => {
        try {
            await adminApi.invalidateCache();
            // Refresh all data
            await Promise.all([
                refreshDocTypes(),
                refreshRoles(),
                refreshTemplates(),
                refreshWorkflows(),
                refreshFragments(),
                refreshSchemas(),
                refreshMechanicalOps(),
                refreshState(),
            ]);
        } catch (err) {
            console.error('Failed to reload config:', err);
            alert(`Failed to reload config: ${err.message}`);
        }
    }, [refreshDocTypes, refreshRoles, refreshTemplates, refreshWorkflows, refreshFragments, refreshSchemas, refreshMechanicalOps, refreshState]);

    // Auth check
    if (authLoading) {
        return (
            <div
                className="flex items-center justify-center h-screen"
                style={{ background: 'var(--bg-canvas)' }}
            >
                <span style={{ color: 'var(--text-muted)' }}>Loading...</span>
            </div>
        );
    }

    if (!isAdmin) {
        return (
            <div
                className="flex items-center justify-center h-screen"
                style={{ background: 'var(--bg-canvas)' }}
            >
                <div
                    className="text-center p-8 rounded-lg"
                    style={{ background: 'var(--bg-panel)', border: '1px solid var(--border-panel)' }}
                >
                    <div
                        className="text-lg font-semibold mb-2"
                        style={{ color: 'var(--state-error-text)' }}
                    >
                        Access Denied
                    </div>
                    <div style={{ color: 'var(--text-muted)' }}>
                        Admin access required for the Workbench.
                    </div>
                </div>
            </div>
        );
    }

    // Workspace initialization
    if (workspaceLoading) {
        return (
            <div
                className="flex items-center justify-center h-screen"
                style={{ background: 'var(--bg-canvas)' }}
            >
                <div className="text-center">
                    <div
                        className="text-lg mb-2"
                        style={{ color: 'var(--text-primary)' }}
                    >
                        Initializing Workspace
                    </div>
                    <div style={{ color: 'var(--text-muted)' }}>
                        Setting up your editing environment...
                    </div>
                </div>
            </div>
        );
    }

    // Workspace error
    if (workspaceError) {
        return (
            <div
                className="flex items-center justify-center h-screen"
                style={{ background: 'var(--bg-canvas)' }}
            >
                <div
                    className="text-center p-8 rounded-lg max-w-md"
                    style={{ background: 'var(--bg-panel)', border: '1px solid var(--border-panel)' }}
                >
                    <div
                        className="text-lg font-semibold mb-2"
                        style={{ color: 'var(--state-error-text)' }}
                    >
                        Workspace Error
                    </div>
                    <div className="mb-4" style={{ color: 'var(--text-muted)' }}>
                        {workspaceError}
                    </div>
                    <button
                        onClick={reinitialize}
                        className="px-4 py-2 rounded text-sm"
                        style={{
                            background: 'var(--action-primary)',
                            color: '#000',
                        }}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // Determine which editor to show
    const showRoleEditor = selectedRole && !selectedDocType && !selectedTemplate && !selectedWorkflow && !selectedFragment && !selectedSchema && !selectedMechanicalOp;
    const showTemplateEditor = selectedTemplate && !selectedDocType && !selectedRole && !selectedWorkflow && !selectedFragment && !selectedSchema && !selectedMechanicalOp;
    const showWorkflowEditor = selectedWorkflow && !selectedDocType && !selectedRole && !selectedTemplate && !selectedFragment && !selectedSchema && !selectedMechanicalOp;
    const showFragmentEditor = selectedFragment && !selectedDocType && !selectedRole && !selectedTemplate && !selectedWorkflow && !selectedSchema && !selectedMechanicalOp;
    const showSchemaEditor = selectedSchema && !selectedDocType && !selectedRole && !selectedTemplate && !selectedWorkflow && !selectedFragment && !selectedMechanicalOp;
    const showMechanicalOpEditor = selectedMechanicalOp && !selectedDocType && !selectedRole && !selectedTemplate && !selectedWorkflow && !selectedFragment && !selectedSchema;
    const showPromptEditor = !showRoleEditor && !showTemplateEditor && !showWorkflowEditor && !showFragmentEditor && !showSchemaEditor && !showMechanicalOpEditor;

    return (
        <div
            className="flex flex-col h-screen overflow-hidden"
            style={{ background: 'var(--bg-canvas)' }}
        >
            {/* Workbench Header */}
            <div
                className="flex items-center justify-between px-4 py-2"
                style={{
                    background: 'var(--bg-group-header)',
                    borderBottom: '1px solid var(--border-panel)',
                    flexShrink: 0,
                }}
            >
                <div className="flex items-center gap-3">
                    <img
                        src="/logo-dark.png"
                        alt="The Combine"
                        className="h-6 cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => { window.location.href = '/'; }}
                        title="Return to Home"
                    />
                    <span
                        className="font-bold uppercase tracking-widest"
                        style={{ color: 'var(--text-primary)', fontSize: 11 }}
                    >
                        Admin Workbench
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleReloadConfig}
                        className="flex items-center gap-1 px-2 py-1 rounded hover:opacity-80 transition-opacity"
                        style={{
                            background: 'var(--bg-panel)',
                            color: 'var(--text-secondary)',
                            border: '1px solid var(--border-panel)',
                            cursor: 'pointer',
                            fontSize: 11,
                        }}
                        title="Reload config from disk (invalidate cache)"
                    >
                        <span style={{ fontSize: 12 }}>&#8635;</span>
                        <span>Reload</span>
                    </button>
                <button
                    onClick={() => setIsTrayOpen(!isTrayOpen)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded hover:opacity-80 transition-opacity"
                    style={{
                        background: isTrayOpen ? 'var(--action-primary)' : 'var(--bg-panel)',
                        color: isTrayOpen ? '#000' : 'var(--text-secondary)',
                        border: '1px solid var(--border-panel)',
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 500,
                    }}
                    title="Building Blocks"
                >
                    <span style={{ fontSize: 14 }}>&#9881;</span>
                    <span>Building Blocks</span>
                </button>
                </div>
            </div>

            {/* Main content area */}
            <div className="flex flex-1 overflow-hidden">
                {/* Left panel - Document Type, Role & Template Browser */}
                <DocTypeBrowser
                documentTypes={documentTypes}
                roles={roles}
                templates={templates}
                workflows={workflows}
                promptFragments={promptFragments}
                promptFragmentKindOptions={promptFragmentKindOptions}
                schemas={schemas}
                loading={docTypesLoading}
                rolesLoading={rolesLoading}
                templatesLoading={templatesLoading}
                workflowsLoading={workflowsLoading}
                promptFragmentsLoading={promptFragmentsLoading}
                schemasLoading={schemasLoading}
                selectedDocType={selectedDocType}
                docTypeSource={docTypeSource}
                selectedRole={selectedRole}
                selectedTemplate={selectedTemplate}
                selectedWorkflow={selectedWorkflow}
                selectedFragment={selectedFragment}
                selectedSchema={selectedSchema}
                onSelectDocType={handleSelectDocType}
                onSelectRole={handleSelectRole}
                onSelectTemplate={handleSelectTemplate}
                onSelectWorkflow={handleSelectWorkflow}
                onSelectFragment={handleSelectFragment}
                onSelectStandaloneSchema={handleSelectStandaloneSchema}
                onCreateWorkflow={handleCreateWorkflow}
                onCreateDocType={handleCreateDocType}
                onCreateFragment={handleCreateFragment}
                onCreateTemplate={handleCreateTemplate}
                onCreateSchema={handleCreateSchema}
                onSelectTask={handleSelectTask}
                onSelectSchema={handleSelectSchema}
                workspaceState={workspaceState}
            />

            {/* Center panel - Editor (Prompt, Role, Template, Fragment, or Workflow) */}
            {showRoleEditor ? (
                <RoleEditor
                    workspaceId={workspaceId}
                    role={selectedRole}
                    onArtifactSave={handleArtifactSave}
                />
            ) : showTemplateEditor ? (
                <TemplateEditor
                    workspaceId={workspaceId}
                    template={selectedTemplate}
                    onArtifactSave={handleArtifactSave}
                />
            ) : showFragmentEditor ? (
                <PromptFragmentEditor
                    workspaceId={workspaceId}
                    fragment={selectedFragment}
                    onArtifactSave={handleArtifactSave}
                />
            ) : showWorkflowEditor ? (
                <StepWorkflowEditor
                    workspaceId={workspaceId}
                    workflow={selectedWorkflow}
                    documentTypes={documentTypes}
                    onArtifactSave={handleArtifactSave}
                    onDelete={handleDeleteWorkflow}
                    onNavigateToWorkflow={handleNavigateToWorkflow}
                />
            ) : showSchemaEditor ? (
                <SchemaEditor
                    workspaceId={workspaceId}
                    schema={selectedSchema}
                    onArtifactSave={handleArtifactSave}
                />
            ) : showMechanicalOpEditor ? (
                <MechanicalOpEditor
                    mechanicalOp={selectedMechanicalOp}
                    mechanicalOpTypes={mechanicalOpTypes}
                />
            ) : (
                <PromptEditor
                    workspaceId={workspaceId}
                    docType={selectedDocTypeDetails}
                    loading={detailsLoading}
                    roles={roles}
                    workflows={workflows}
                    onArtifactSave={handleArtifactSave}
                    onCreateDcwWorkflow={handleCreateDcwWorkflow}
                    initialTab={initialTab}
                    docTypeSource={docTypeSource}
                    mechanicalOpTypes={mechanicalOpTypes}
                    mechanicalOps={mechanicalOps}
                />
            )}

                {/* Right panel - Git Status */}
                <GitStatusPanel
                    workspaceId={workspaceId}
                    state={workspaceState}
                    loading={stateLoading}
                    onCommit={handleCommit}
                    onDiscard={handleDiscard}
                    onClose={handleClose}
                    onRefresh={refreshState}
                />
            </div>

            {/* Building Blocks Tray (WS-ADR-044-003) */}
            <BuildingBlocksTray
                isOpen={isTrayOpen}
                onClose={() => setIsTrayOpen(false)}
                promptFragments={promptFragments}
                promptFragmentKindOptions={promptFragmentKindOptions}
                templates={templates}
                schemas={schemas}
                documentTypes={documentTypes}
                promptFragmentsLoading={promptFragmentsLoading}
                templatesLoading={templatesLoading}
                schemasLoading={schemasLoading}
                selectedFragment={selectedFragment}
                selectedTemplate={selectedTemplate}
                selectedSchema={selectedSchema}
                selectedMechanicalOp={selectedMechanicalOp}
                onSelectFragment={handleSelectFragment}
                onSelectTemplate={handleSelectTemplate}
                onSelectSchema={handleSelectSchema}
                onSelectStandaloneSchema={handleSelectStandaloneSchema}
                onCreateFragment={handleCreateFragment}
                onCreateTemplate={handleCreateTemplate}
                onCreateSchema={handleCreateSchema}
                mechanicalOpTypes={mechanicalOpTypes}
                mechanicalOps={mechanicalOps}
                mechanicalOpsLoading={mechanicalOpsLoading}
                onSelectMechanicalOp={handleSelectMechanicalOp}
            />
        </div>
    );
}
